/**
 * useAppVersion —— Admin 后台版本刷新提示
 *
 * 周期性轮询 /version.json（构建期写入，含部署时间戳），
 * 与注入到代码中的 __APP_VERSION__ 对比；不一致则标记「有新版本」，
 * 由布局组件渲染提示条，引导运营人员刷新页面拿到最新后台。
 *
 * 解决「部署后 index.html/JS 被浏览器缓存，后台看不到新功能/新数据字段」的问题。
 */

import { useState, useEffect, useCallback, useRef } from 'react'

/** /version.json 的返回结构 */
interface VersionPayload {
  version: string
}

interface UseAppVersionReturn {
  /** 是否有新版本 */
  hasNewVersion: boolean
  /** 当前运行版本 */
  currentVersion: string
  /** 线上最新版本 */
  remoteVersion: string | null
  /** 手动检测 */
  check: () => Promise<void>
}

const CHECK_INTERVAL_MS = 30 * 1000 // 30 秒轮询一次

export function useAppVersion(): UseAppVersionReturn {
  const [hasNewVersion, setHasNewVersion] = useState(false)
  const [remoteVersion, setRemoteVersion] = useState<string | null>(null)
  const currentVersion = __APP_VERSION__
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' })
      if (!res.ok) return
      const data: VersionPayload = await res.json()
      if (typeof data.version === 'string' && data.version !== currentVersion) {
        setRemoteVersion(data.version)
        setHasNewVersion(true)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    } catch {
      // 轮询失败静默忽略，避免弹错打扰运营
    }
  }, [currentVersion])

  useEffect(() => {
    checkVersion()
    intervalRef.current = setInterval(checkVersion, CHECK_INTERVAL_MS)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [checkVersion])

  return {
    hasNewVersion,
    currentVersion,
    remoteVersion,
    check: checkVersion,
  }
}
